import { useCallback, useEffect, useState } from 'react';
import { captureTabScreenshot, type TabScreenshot } from '@sorrem-client/ui/telemetry';

export interface TabScreenshotState {
    /** The attached screenshot, or null. */
    screenshot: TabScreenshot | null;
    /** True from the click until the frame is taken: hide the form meanwhile, so it isn't in the picture. */
    isCapturing: boolean;
    /** Why the last capture failed, or null. Declining Chrome's prompt is not a failure. */
    error: string | null;
    /** Captures the tab; call it from a click. */
    capture: () => Promise<void>;
    remove: () => void;
}

/** A whole-tab screenshot a tester can attach to a bug report (#235). */
export function useTabScreenshot(): TabScreenshotState {
    const [screenshot, setScreenshot] = useState<TabScreenshot | null>(null);
    const [isCapturing, setIsCapturing] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // The preview's object URL is released whenever the screenshot changes or the form closes.
    useEffect(() => () => {
        if (screenshot) {
            URL.revokeObjectURL(screenshot.previewUrl);
        }
    }, [screenshot]);

    const capture = useCallback(async (): Promise<void> => {
        setError(null);
        setIsCapturing(true);
        try {
            // Two frames: one to commit the hidden form, one for the browser to paint without it.
            await new Promise<void>((resolveFrame) => requestAnimationFrame(() => requestAnimationFrame(() => resolveFrame())));
            const captured = await captureTabScreenshot();
            if (captured) {
                setScreenshot(captured);
            }
        } catch (failure) {
            setError(failure instanceof Error ? failure.message : 'The screenshot could not be taken.');
        } finally {
            setIsCapturing(false);
        }
    }, []);

    const remove = useCallback((): void => setScreenshot(null), []);

    return { screenshot, isCapturing, error, capture, remove };
}
